import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/features/auth/store/auth-store';
import { useMyOnboardingStatus } from '@/api/hooks/use-users';
import { useOnboardingDraftStore } from '../store/onboarding-draft-store';

const ONBOARDING_PATH_PREFIX = '/onboarding';
const TERMINAL_PATH = '/venue-connections';

/**
 * Redirects fresh tenants to `/onboarding/welcome` after first auth.
 *
 * Logic:
 * 1. If the user is on any `/onboarding/*` route → render `<Outlet />`.
 * 2. Else, fetch `OnboardingStatus`. If `all_complete === false` AND the
 *    wizard draft hasn't been completed locally → redirect to onboarding.
 * 3. Otherwise pass through.
 *
 * The local draft (`lastCompleted === 4`) is the source of truth for "wizard
 * was completed by this user" — backend `OnboardingSteps` tracks venue/bot
 * provisioning, not wizard sign-off (Phase-1 backend gap).
 */
export function OnboardingGuard() {
  const location = useLocation();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const lastCompleted = useOnboardingDraftStore((s) => s.lastCompleted);
  const { data: status, isLoading } = useMyOnboardingStatus();

  if (!isAuthenticated) return <Outlet />;
  if (location.pathname.startsWith(ONBOARDING_PATH_PREFIX)) return <Outlet />;
  if (isLoading) return <Outlet />;

  const wizardSigned = lastCompleted >= 4;
  const backendComplete = status?.onboarding?.all_complete === true;

  // If both signal "done", or just the wizard is done, never redirect.
  if (wizardSigned || backendComplete) return <Outlet />;

  // Fresh tenant: redirect once to the wizard.
  return <Navigate to="/onboarding/welcome" replace state={{ from: location }} />;
}

export { TERMINAL_PATH };
